import type { StatusVariants } from "@/components/molecules";

export interface ApiExtraError {
  code: string;
  description: string;
  property: string;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  property?: string;
  extra?: Record<string, unknown>;
  errors?: ApiExtraError[];
}

export interface PaginationParams {
  limit: number;
  offset: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  query?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  query: string;
  hasMore: boolean;
  offset: number;
}

export enum TransactionStatus {
  PENDING = "PENDING",
  INITIATED = "INITIATED",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
  FULLY_PAID = "FULLY_PAID",
  PARTIALLY_PAID = "PARTIALLY_PAID",
  EXPIRED = "EXPIRED",
  DECLINED = "DECLINED",
  ABORTED = "ABORTED",
  VOIDED = "VOIDED",
}

export const STATUS_VARIANT_MAP: StatusVariants = {
  Initiated: { badge: "outline", dot: "#D2D5D9" },
  Pending: { badge: "info", dot: "#D97706" },
  Failed: { badge: "warning", dot: "#DC2626" },
  Cancelled: { badge: "default", dot: "#111827" },
  Expired: { badge: "warning", dot: "#DC2626" },
  Declined: { badge: "warning", dot: "#DC2626" },
  Aborted: { badge: "warning", dot: "#DC2626" },
  Voided: { badge: "default", dot: "#111827" },
  "Fully Paid": { badge: "success", dot: "#65A30D" },
  "Partially Paid": { badge: "info", dot: "#D97706" },
};

export const transactionStatusMap: Record<string, TransactionStatus> = {
  Initiated: TransactionStatus.INITIATED,
  Pending: TransactionStatus.PENDING,
  Failed: TransactionStatus.FAILED,
  Cancelled: TransactionStatus.CANCELLED,
  Expired: TransactionStatus.EXPIRED,
  Declined: TransactionStatus.DECLINED,
  Aborted: TransactionStatus.ABORTED,
  Voided: TransactionStatus.VOIDED,
  "Fully Paid": TransactionStatus.FULLY_PAID,
  "Partially Paid": TransactionStatus.PARTIALLY_PAID,
};

export enum PartialState {
  partial = "Partially Paid",
  full = "Fully Paid",
}

export const STATUS_MAP_RUFUND: StatusVariants = {
  "Fully Refunded": {
    badge: "success",
    dot: "#65A30D",
  },
  "Partially Refunded": {
    badge: "info",
    dot: "#D97706",
  },
};

export const STATUS_MAP_IS_PARTIAL_TRANSACTIONS: StatusVariants = {
  [PartialState.full]: { badge: "success", dot: "#65A30D" },
  [PartialState.partial]: { badge: "info", dot: "#D97706" },
  ...STATUS_MAP_RUFUND,
};

export enum REFUND_TRANSACTION_STATUS {
  PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
  FULLY_REFUNDED = "FULLY_REFUNDED",
}

export const STATUS_MAP_RUFUND_TRANSACTIONS: StatusVariants = {
  [REFUND_TRANSACTION_STATUS.FULLY_REFUNDED]: {
    badge: "success",
    dot: "#65A30D",
  },
  [REFUND_TRANSACTION_STATUS.PARTIALLY_REFUNDED]: {
    badge: "info",
    dot: "#D97706",
  },
};

export const STATUS_MAP_TRANSACTIONS_COMBINED: StatusVariants = {
  ...Object.fromEntries(Object.entries(STATUS_VARIANT_MAP)),
  ...STATUS_MAP_RUFUND,
};

export const transactionCombinedStatusMap: Record<
  string,
  TransactionStatus | PartialState
> = {
  ...Object.fromEntries(Object.entries(transactionStatusMap)),
};

export const refundCombinedStatusMap: Record<
  string,
  REFUND_TRANSACTION_STATUS
> = {
  "Fully Refunded": REFUND_TRANSACTION_STATUS.FULLY_REFUNDED,
  "Partially Refunded": REFUND_TRANSACTION_STATUS.PARTIALLY_REFUNDED,
};

export const STATUS_VARIANT_MAP_TRANSACTIONS: StatusVariants = {
  [TransactionStatus.COMPLETED]: { badge: "success", dot: "#65A30D" },
  [TransactionStatus.INITIATED]: { badge: "outline", dot: "#D2D5D9" },
  [TransactionStatus.PENDING]: { badge: "info", dot: "#D97706" },
  [TransactionStatus.FAILED]: { badge: "warning", dot: "#DC2626" },
  [TransactionStatus.CANCELLED]: { badge: "default", dot: "#111827" },
  [TransactionStatus.FULLY_PAID]: { badge: "success", dot: "#65A30D" },
  [TransactionStatus.PARTIALLY_PAID]: { badge: "info", dot: "#D97706" },
  [TransactionStatus.EXPIRED]: { badge: "warning", dot: "#DC2626" },
  [TransactionStatus.DECLINED]: { badge: "warning", dot: "#DC2626" },
  [TransactionStatus.ABORTED]: { badge: "warning", dot: "#DC2626" },
  [TransactionStatus.VOIDED]: { badge: "default", dot: "#111827" },
};

export interface AuthTokens {
  token: string;
  refreshToken: string;
}
